/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {error} from '@ion/core/src/logger/logger';
import {
  createSchedule,
  deleteSchedule as destroySchedule,
  loadSchedules,
  updateSchedule
} from '@ion/core/src/services/Maintenance';
import {
  MaintenanceSchedule
} from '@ion/core/src/types/entities';
import {LoadMaintenanceSchedulesParams} from '@ion/core/src/types/services';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {getErrorMessage, translatePromptAction} from '@ion/dls/src/utils/ComponentUtils';
import {
  AsyncActionCreator,
  PageInitializeAction
} from '@/types/redux';
import {
  errorAddOptions,
  errorEditOptions,
  successAddOptions,
  successEditOptions
} from '../../routes/admin/maintenance/schedules/AdminMaintenanceSchedulesUtils';
import {getAccountId} from '../helpers';
import {toggleUserPrompt} from './user-prompt';

// name of the reducer
export const NAME                                              = 'adminMaintenanceSchedules';

export const INITIALIZE_ADMIN_MAINTENANCE_SCHEDULES            = 'INITIALIZE_ADMIN_MAINTENANCE_SCHEDULES';
export const RE_INITIALIZE_ADMIN_MAINTENANCE_SCHEDULES         = 'RE_INITIALIZE_ADMIN_MAINTENANCE_SCHEDULES';

// grid column state
export const SET_GRID_COLUMNS                                  = 'SET_ADMIN_MAINTENANCE_SCHEDULES_GRID_COLUMNS';
export const UPDATE_GRID_COLUMNS                               = 'UPDATE_ADMIN_MAINTENANCE_SCHEDULES_GRID_COLUMNS';

export const ADMIN_MAINTENANCE_SCHEDULES_GRID_REFRESHED        = 'ADMIN_MAINTENANCE_SCHEDULES_GRID_REFRESHED';
// schedules retrieval ...
export const GET_ADMIN_MAINTENANCE_SCHEDULES_REQUEST           = 'GET_ADMIN_MAINTENANCE_SCHEDULES_REQUEST';
export const GET_ADMIN_MAINTENANCE_SCHEDULES_RESPONSE          = 'GET_ADMIN_MAINTENANCE_SCHEDULES_RESPONSE';
export const GET_ADMIN_MAINTENANCE_SCHEDULES_ERROR             = 'GET_ADMIN_MAINTENANCE_SCHEDULES_ERROR';

// schedule add ...
export const ADD_ADMIN_MAINTENANCE_SCHEDULE                    = 'ADD_ADMIN_MAINTENANCE_SCHEDULE';
export const ADD_ADMIN_MAINTENANCE_SCHEDULE_REQUEST            = 'ADD_ADMIN_MAINTENANCE_SCHEDULE_REQUEST';
export const ADD_ADMIN_MAINTENANCE_SCHEDULE_RESPONSE           = 'ADD_ADMIN_MAINTENANCE_SCHEDULE_RESPONSE';
export const ADD_ADMIN_MAINTENANCE_SCHEDULE_ERROR              = 'ADD_ADMIN_MAINTENANCE_SCHEDULE_ERROR';

// schedule edit ...
export const EDIT_ADMIN_MAINTENANCE_SCHEDULE                   = 'EDIT_ADMIN_MAINTENANCE_SCHEDULE';
export const EDIT_ADMIN_MAINTENANCE_SCHEDULE_REQUEST           = 'EDIT_ADMIN_MAINTENANCE_SCHEDULE_REQUEST';
export const EDIT_ADMIN_MAINTENANCE_SCHEDULE_RESPONSE          = 'EDIT_ADMIN_MAINTENANCE_SCHEDULE_RESPONSE';
export const EDIT_ADMIN_MAINTENANCE_SCHEDULE_ERROR             = 'EDIT_ADMIN_MAINTENANCE_SCHEDULE_ERROR';

// schedule delete ...
export const DELETE_ADMIN_MAINTENANCE_SCHEDULE                 = 'DELETE_ADMIN_MAINTENANCE_SCHEDULE';
export const RESET_DELETE_ADMIN_MAINTENANCE_SCHEDULE           = 'RESET_DELETE_ADMIN_MAINTENANCE_SCHEDULE';
export const DELETE_ADMIN_MAINTENANCE_SCHEDULE_REQUEST         = 'DELETE_ADMIN_MAINTENANCE_SCHEDULE_REQUEST';
export const DELETE_ADMIN_MAINTENANCE_SCHEDULE_RESPONSE        = 'DELETE_ADMIN_MAINTENANCE_SCHEDULE_RESPONSE';
export const DELETE_ADMIN_MAINTENANCE_SCHEDULE_ERROR           = 'DELETE_ADMIN_MAINTENANCE_SCHEDULE_ERROR';

// admin maintenance schedules filter model ...
export const SET_ADMIN_MAINTENANCE_SCHEDULES_GRID_FILTER_MODEL = 'SET_ADMIN_MAINTENANCE_SCHEDULES_GRID_FILTER_MODEL';

/**
 * @initialize
 */
export const initializeAdminSchedules = (): PageInitializeAction => {
  return {type: INITIALIZE_ADMIN_MAINTENANCE_SCHEDULES};
};

export const reInitializeAdminSchedules = (): PageInitializeAction => {
  return {type: RE_INITIALIZE_ADMIN_MAINTENANCE_SCHEDULES};
};

/**
 * Invoked when the Grid is to be refreshed.
 */
export const adminMaintenanceSchedulesGridRefreshed = (): PageInitializeAction => {
  return {type: ADMIN_MAINTENANCE_SCHEDULES_GRID_REFRESHED};
};

/**
 * @getAdminMaintenanceSchedules
 *
 * Invoked when the paginated list of Maintenance Schedules is to be retrieved.
 */
export const getAdminMaintenanceSchedules = ({
  pageId = 1,
  pageSize = 20,
  sort,
  filter,
  includeSubGroups = false,
  callback: callbackFn
}) => {
  return async(dispatch: Function, getState: any): Promise<any> => {
    dispatch({type: GET_ADMIN_MAINTENANCE_SCHEDULES_REQUEST});
    const params: LoadMaintenanceSchedulesParams = {
      accountId : getAccountId(getState),
      hierarchy : includeSubGroups,
      filters   : filter,
      pageId,
      pageSize,
      sort
    };
    try {
      const {schedules: items, totalCount} = await loadSchedules(params);
      dispatch({type: GET_ADMIN_MAINTENANCE_SCHEDULES_RESPONSE});
      callbackFn?.({items, totalCount});
      return {items, totalCount};
    } catch (err) {
      error(err);
      dispatch({type: GET_ADMIN_MAINTENANCE_SCHEDULES_ERROR, error: err});
      return err;
    }
  };
};

/**
 * @addAdminMaintenanceSchedule
 *
 * Invoked when the Add Admin Maintenance Schedule editor is to be displayed or removed.
 */
export const addAdminMaintenanceSchedule = (show = true) => ({
  type : ADD_ADMIN_MAINTENANCE_SCHEDULE, show
});

/**
 * @addMaintenanceSchedule
 *
 * Invoked when a new Maintenance Schedule is to be created; consolidating the process of
 * creation into a single transaction.
 */
export const addMaintenanceSchedule =
(
  schedule: MaintenanceSchedule,
  isReadOnlyUser: boolean = false
): AsyncActionCreator => {
  return async(dispatch: Function): Promise<void> => {
    dispatch({type: ADD_ADMIN_MAINTENANCE_SCHEDULE_REQUEST, schedule});
    try {
      const created = await createSchedule(schedule);
      dispatch({type: ADD_ADMIN_MAINTENANCE_SCHEDULE_RESPONSE, schedule: created});
      dispatch(toggleUserPrompt(successAddOptions));
    } catch (err) {
      dispatch({type: ADD_ADMIN_MAINTENANCE_SCHEDULE_ERROR, error: err});
      dispatch(toggleUserPrompt({
        ...errorAddOptions,
        ...({actionLabel: translatePromptAction('common:retry', isReadOnlyUser)}),
        message : getErrorMessage(err)
      }));
      error(err);
    }
  };
};

/**
 * @editAdminMaintenanceSchedule
 *
 * Invoked when the Edit Admin Maintenance Schedule editor is to be displayed or removed.
 */
export const editAdminMaintenanceSchedule = (schedule: MaintenanceSchedule, show = true) => ({
  type : EDIT_ADMIN_MAINTENANCE_SCHEDULE, schedule, show
});

/**
 * @editMaintenanceSchedule
 *
 * Invoked when an existing Maintenance Schedule is to be updated; consolidating the process
 * of updating into a single transaction.
 */
export const editMaintenanceSchedule = (schedule: MaintenanceSchedule, isReadOnlyUser: boolean = false): Function => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_ADMIN_MAINTENANCE_SCHEDULE_REQUEST});
    try {
      const updated = await updateSchedule(schedule);
      dispatch({type: EDIT_ADMIN_MAINTENANCE_SCHEDULE_RESPONSE, schedule: updated});
      dispatch(toggleUserPrompt(successEditOptions));
    } catch (err) {
      dispatch({type: EDIT_ADMIN_MAINTENANCE_SCHEDULE_ERROR, error: err});
      dispatch(toggleUserPrompt({
        ...errorEditOptions,
        ...({actionLabel: translatePromptAction('common:retry', isReadOnlyUser)}),
        message : getErrorMessage(err)
      }));
      error(err);
    }
  };
};

/**
 * @deleteAdminMaintenanceSchedule
 *
 * Invoked when the Delete Admin Maintenance Schedule Dialog is to be displayed or removed.
 */
export const deleteAdminMaintenanceSchedule = (schedule: MaintenanceSchedule, show = true) => ({
  type : DELETE_ADMIN_MAINTENANCE_SCHEDULE, schedule, show
});

/**
 * @resetDeleteAdminMaintenanceSchedule
 *
 * Invoked when the adminMaintenanceSchedule -> destroy -> result needs to be reset..
 */
export const resetDeleteAdminMaintenanceSchedule = () => ({
  type : RESET_DELETE_ADMIN_MAINTENANCE_SCHEDULE
});

// Invoked to reset/remove admin maintainance Schedule error.
export const removeAdminMaintenanceScheduleError = (error) => ({
  type : DELETE_ADMIN_MAINTENANCE_SCHEDULE_ERROR, error : error
});

/**
 * @deleteMaintenanceSchedule
 *
 * Invoked when an existing Maintenance Schedule is to be deleted.
 */
export const deleteMaintenanceSchedule = (schedule: MaintenanceSchedule) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_ADMIN_MAINTENANCE_SCHEDULE_REQUEST});
    try {
      const deleted = await destroySchedule(schedule.scheduleId);
      dispatch({type: DELETE_ADMIN_MAINTENANCE_SCHEDULE_RESPONSE, schedule: deleted});
    } catch (err) {
      dispatch({type: DELETE_ADMIN_MAINTENANCE_SCHEDULE_ERROR, error: err});
      dispatch(toggleUserPrompt({message: getErrorMessage(err)}));
      error(err);
    }
  };
};

// Invoked by IonGrid when the grid filter model changes.
export const setFilterModel = (model: FilterModel) => ({
  type : SET_ADMIN_MAINTENANCE_SCHEDULES_GRID_FILTER_MODEL, model
});
